import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { endSharedSession, RudderstackAuthError } from '@/auth/lib/rudderstack-auth-client';

import type { WorkspaceInfo } from '../contexts/WorkspaceContext';
import { clearAccountDrafts } from '../lib/account-drafts';
import { clearLastWorkspaceId } from '../lib/last-workspace';
import { orpc } from '../lib/orpc';
import { rememberReturnAfterSignOut } from '../lib/return-after-sign-out';

const LogoutBodySchema = z.object({ redirectTo: z.string().min(1) });

/** The address, when it is a web address. */
function webAddress(value: string): string | null {
  try {
    const url = new URL(value, window.location.origin);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch {
    return null;
  }
}

/** The provider sign-out page a Google or SSO session must also visit (ACT2-871), if the server names one. */
function providerSignOutUrl(body: unknown): string | null {
  const parsed = LogoutBodySchema.safeParse(body);
  return parsed.success ? webAddress(parsed.data.redirectTo) : null;
}

/** Options for a sign-out. */
interface SignOutOptions {
  /** Stay signed in at the Google or SSO provider. */
  keepProviderSession?: boolean;
  /** Leave config-backend's shared session alone, because it already ended (ACT2-879). */
  keepSharedSession?: boolean;
}

/** A refusal that proves there is no shared session to end here: an untrusted origin could not start one either. */
function isRefusedOrigin(error: unknown): boolean {
  return error instanceof RudderstackAuthError && (error.status === 403 || error.status === 415);
}

interface UseAuthReturn {
  user: {
    id: string;
    githubLogin: string | null;
    githubName: string | null;
    githubAvatarUrl: string | null;
    email: string | null;
    displayName: string | null;
    avatarUrl: string | null;
  } | null;
  workspace: WorkspaceInfo | null;
  workspaces: WorkspaceInfo[];
  /**
   * The self-serve demo workspace, offered in the create-workspace modal — non-null only when the
   * deployment has one configured AND the caller isn't already a member (server-computed; see
   * `auth.me`). Top-level, not part of `workspace`/`workspaces`: its whole audience is a non-member,
   * who may have no workspace at all.
   */
  demoWorkspace: { slug: string; name: string } | null;
  isOwner: boolean;
  isAdmin: boolean;
  loading: boolean;
  error: boolean;
  /** No user, but the browser still sent a validly signed session cookie: the session ended. */
  sessionEnded: boolean;
  /** Logs out, then loads `to` (default `/login`); a Google/SSO session loads the provider sign-out instead unless kept. */
  signOutAndRedirect: (to?: string, options?: SignOutOptions) => Promise<boolean>;
}

export function useAuth(): UseAuthReturn {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery(orpc.auth.me.queryOptions({ input: {} }));
  const { data: providers } = useQuery(orpc.auth.providers.queryOptions());
  const userId = data?.user?.id;
  const previousUserId = useRef(userId);
  useEffect(() => {
    if (data?.user === null || (previousUserId.current && userId && previousUserId.current !== userId))
      clearAccountDrafts();
    previousUserId.current = userId;
  }, [data, userId]);

  const sharedApiBaseUrl = providers?.nativeSignIn?.sharedSession ? providers.nativeSignIn.apiBaseUrl : null;

  // Null when logout failed; otherwise the provider sign-out page to visit, if any.
  const endSession = useCallback(
    async (options: SignOutOptions = {}): Promise<{ redirectTo: string | null } | null> => {
      // ACT2-879: the shared session ends first. Otherwise the login page would start Lookout again from it, and the
      // web app would stay signed in. Its failure keeps the user signed in, as a failed Lookout logout does.
      let sharedSignOutUrl: string | null = null;
      if (sharedApiBaseUrl && !options.keepSharedSession) {
        try {
          const { hostedUiSignOutUrl } = await endSharedSession(sharedApiBaseUrl);
          sharedSignOutUrl = hostedUiSignOutUrl ? webAddress(hostedUiSignOutUrl) : null;
        } catch (error) {
          if (!isRefusedOrigin(error)) {
            toast.error('Could not sign you out. Please try again.');
            return null;
          }
        }
      }

      // Fail closed (AI-1030): only tear down client auth state once the server confirms the session
      // was revoked. On a network error or a non-OK response (e.g. 500 `logout_incomplete`, where the
      // server kept the still-valid cookie) we surface the error and keep the user signed in, rather
      // than presenting a logged-out UI while the token is still live — the shared-machine risk.
      let body: unknown;
      try {
        const res = await fetch('/auth/logout', { method: 'POST', credentials: 'include' });
        if (!res.ok) {
          toast.error('Could not sign you out. Please try again.');
          return null;
        }
        body = await res.json().catch(() => null);
      } catch {
        toast.error('Could not sign you out. Please try again.');
        return null;
      }

      // Immediately mark user as logged out
      queryClient.setQueryData(orpc.auth.me.queryOptions({ input: {} }).queryKey, {
        user: null,
        workspace: null,
        workspaces: [],
        demoWorkspace: null,
        sessionEnded: false,
        sharedSession: null,
      });
      // Wipe all other cached data to prevent stale session data leakage
      void queryClient.invalidateQueries();
      // …and the MUTATION cache with it (#10). `invalidateQueries` only touches queries, so every
      // finished mutation kept its `variables` and `data` — which for this app includes an account
      // payload's `options`, i.e. the credential the user just typed. The page reloads only after this, so on
      // a shared machine that state must not wait for the reload. Clearing mutations cannot cause the refetch
      // loop that rules out `queryClient.clear()` here: mutations are never refetched.
      queryClient.getMutationCache().clear();
      // Forget the last-visited workspace so the next user on a shared machine isn't auto-entered.
      clearLastWorkspaceId();
      clearAccountDrafts();
      return { redirectTo: sharedSignOutUrl ?? providerSignOutUrl(body) };
    },
    [queryClient, sharedApiBaseUrl],
  );

  const signOutAndRedirect = useCallback(
    async (to = '/login', options: SignOutOptions = {}): Promise<boolean> => {
      // Only redirect once logout actually succeeded; otherwise the still-valid cookie would
      // re-authenticate the next request and bounce the user straight back in.
      const ended = await endSession(options);
      if (!ended) return false;
      const providerSignOut = ended.redirectTo && !options.keepProviderSession ? ended.redirectTo : null;
      // The provider's sign-out page always returns to /login, so the login page gets any other target from here.
      if (providerSignOut && to !== '/login') rememberReturnAfterSignOut(to);
      window.location.assign(providerSignOut ?? to);
      return true;
    },
    [endSession],
  );

  return {
    user: data?.user ?? null,
    workspace: data?.workspace ?? null,
    workspaces: data?.workspaces ?? [],
    demoWorkspace: data?.demoWorkspace ?? null,
    isOwner: isLoading ? true : data?.workspace?.role === 'owner',
    isAdmin: isLoading ? true : data?.workspace?.role === 'owner' || data?.workspace?.role === 'admin',
    loading: isLoading,
    error: isError,
    sessionEnded: data?.user === null && data.sessionEnded,
    signOutAndRedirect,
  };
}
